/**
 * Sound Effects Utility
 * Generates celebration and UI sound effects using Web Audio API
 */

/**
 * Play a fun celebration sound for streak achievements
 * Uses Web Audio API to synthesize upbeat chimes
 */
export function playStreakCelebrationSound(): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    const masterGain = audioContext.createGain()
    masterGain.gain.value = 0.3 // Overall volume
    masterGain.connect(audioContext.destination)

    // Play a sequence of cheerful notes
    const now = audioContext.currentTime
    const notes = [
      { freq: 523.25, time: 0 },      // C5
      { freq: 659.25, time: 0.1 },    // E5
      { freq: 783.99, time: 0.2 },    // G5
      { freq: 1046.50, time: 0.3 },   // C6
      { freq: 1318.51, time: 0.45 },  // E6 (triumphant finish)
    ]

    notes.forEach(({ freq, time }) => {
      playNote(audioContext, masterGain, freq, now + time, 0.15)
    })

    // Add a sparkle effect with higher pitched notes
    const sparkles = [
      { freq: 2093, time: 0.5 },
      { freq: 2349, time: 0.55 },
      { freq: 2637, time: 0.6 },
    ]

    sparkles.forEach(({ freq, time }) => {
      playNote(audioContext, masterGain, freq, now + time, 0.08, 0.15)
    })

  } catch (error) {
    console.log('Audio playback not available:', error)
  }
}

/**
 * Play a single note using oscillators
 */
function playNote(
  context: AudioContext,
  destination: AudioNode,
  frequency: number,
  startTime: number,
  duration: number,
  volume: number = 0.25
): void {
  // Create oscillator for the main tone
  const oscillator = context.createOscillator()
  const gainNode = context.createGain()

  oscillator.type = 'sine'
  oscillator.frequency.value = frequency

  // ADSR envelope for smooth sound
  gainNode.gain.setValueAtTime(0, startTime)
  gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01) // Attack
  gainNode.gain.linearRampToValueAtTime(volume * 0.7, startTime + 0.04) // Decay
  gainNode.gain.setValueAtTime(volume * 0.7, startTime + duration - 0.05) // Sustain
  gainNode.gain.linearRampToValueAtTime(0, startTime + duration) // Release

  oscillator.connect(gainNode)
  gainNode.connect(destination)

  oscillator.start(startTime)
  oscillator.stop(startTime + duration)
}

/**
 * Play a simple success chime
 */
export function playSuccessSound(): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    const masterGain = audioContext.createGain()
    masterGain.gain.value = 0.2
    masterGain.connect(audioContext.destination)

    const now = audioContext.currentTime
    playNote(audioContext, masterGain, 659.25, now, 0.1) // E5
    playNote(audioContext, masterGain, 783.99, now + 0.08, 0.15) // G5
  } catch (error) {
    console.log('Audio playback not available:', error)
  }
}

/**
 * Play a subtle click sound for UI interactions
 */
export function playClickSound(): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.type = 'sine'
    oscillator.frequency.value = 800

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05)

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.05)
  } catch (error) {
    console.log('Audio playback not available:', error)
  }
}
